/* global i18n */
import 'libphonenumber-wrapper';
import { defineMessages } from 'react-intl';
import { intl } from 'hellospa/common/hs-intl-provider';
import * as yup from 'yup';
import { BANNER } from 'hellospa/components/prep-and-send/formik-utils';
import { getIn } from 'formik';
import * as uuid from 'uuid';
import { getFormattedPhoneOptions } from 'libphonenumber-wrapper/utils';
import countriesMessages, { CountryCode } from 'common/constants/countries';
import { AppActions } from 'hello-react/web-app-client';
import { EmailValidationContextShape } from 'js/sign-components/recipient/email-validation-context';

interface FormattedPhoneOption {
  region: string;
  country: string;
  number: string;
}

type TestContext = {
  appActions?: AppActions,
  useEmailDeliverabilityValidation?: boolean,
  isSubmitting?: boolean,
  emailValidationContext?: EmailValidationContextShape,
  useSupportedCountriesSmsValidation?: boolean,
  useUpdatedLibPhoneNumberLibrary?: boolean,
}

declare module 'yup' {
  interface ArraySchema<T> {
    unique(
      paths: Array<keyof T>,
    ): ArraySchema<T>,
    removeEmpty(
      fields: Array<keyof T>,
      options?: { alwaysFilter?: boolean },
    ): ArraySchema<T>,
  }

  interface StringSchema {
    sequence(
      fields: Array<Function>,
    ): StringSchema<string>,
  }
}

const messages = defineMessages({
  errorPhoneNumberInvalid: {
    id: '',
    description: 'Error message for invalid phone number',
    defaultMessage: 'Number is invalid',
  },
  errorCountryNotSupported: {
    id: '',
    description: 'Error message for unsupported country code',
    defaultMessage: 'Numbers from {country} are not supported',
  },
  errorEmailInvalid: {
    id: '',
    description: 'Error message for invalid email',
    defaultMessage: 'Email format is invalid',
  },
  errorNotUnique: {
    id: '',
    description: 'Error message for non-uniqueness',
    defaultMessage: '{label} must be unique',
  },
});

// https://hellosign.atlassian.net/browse/DEV-27512?focusedCommentId=212881
const supportedCountriesForSendingSMS = ['US', 'AU', 'CA', 'IT'];

let phoneUtil: any;
const getPhoneOptions = (value: string, contextFlag?: boolean, paramFlag?: boolean) => {
  const useUpdatedLibPhoneNumberLibrary = Boolean(contextFlag) || Boolean(paramFlag);
  if (useUpdatedLibPhoneNumberLibrary) {
    return getFormattedPhoneOptions(value);
  } else {
    return phoneUtil.getFormattedPhoneOptions(value, phoneUtil);
  }
};

const testPlainPhoneNumber = (useUpdatedLibPhoneNumber?: boolean) => yup.string().test(
  'phone-number',
  intl.formatMessage(messages.errorPhoneNumberInvalid),
  function test(value: string) {
    const context = this.options.context as TestContext;
    if (value) {
      const opts = getPhoneOptions(
        value,
        context?.useUpdatedLibPhoneNumberLibrary,
        useUpdatedLibPhoneNumber,
      );
      return opts.length > 0;
    }
    // I'm considering an empty value as valid so that .required() complains
    // instead.
    return true;
  },
);

const testPhoneNumberWithSupportedCountriesSmsValidation = (
  useUpdatedLibPhoneNumber?: boolean,
) => yup.string().test(
  'phone-number-with-supported-countries-validation',
  'phone-number-with-supported-countries-validation-error',
  function test(value: string) {
    const context = this.options?.context as TestContext;
    if (value) {
      const opts = getPhoneOptions(
        value,
        context?.useUpdatedLibPhoneNumberLibrary,
        useUpdatedLibPhoneNumber,
      ) as FormattedPhoneOption[];
      if (opts.length > 0) {
        const { region } = opts[0];
        const country = intl.formatMessage(countriesMessages[region as CountryCode]);
        // Check if the country exists in our supported countries array.
        if (!opts.some(({ region }) => supportedCountriesForSendingSMS.includes(region))) {
          return this.createError({
            message: intl.formatMessage(messages.errorCountryNotSupported, { country }),
          });
        } else {
          return true;
        }
      }
      return this.createError({
        message: intl.formatMessage(messages.errorPhoneNumberInvalid),
      });
    }
    return true;


  },
);

export function phoneNumber(
  useUpdatedLibPhoneNumber?: boolean,
): yup.StringSchema<string> {
  if (!phoneUtil) {
    phoneUtil = i18n.phonenumbers.PhoneNumberUtil.getInstance();
  }
  // in order to validate phone number against supported country codes
  // options.context.useSupportedCountriesSmsValidation needs to be set to true
  // (in when() condition, '$' is used for context lookup)
  return yup.string().when('$useSupportedCountriesSmsValidation', {
    is: true,
    then: testPhoneNumberWithSupportedCountriesSmsValidation(useUpdatedLibPhoneNumber),
    otherwise: testPlainPhoneNumber(useUpdatedLibPhoneNumber),
  }).transform((value, testContext) => {
    if (value) {
      const opts = getPhoneOptions(
        value,
        testContext?.options?.context?.useUpdatedLibPhoneNumberLibrary,
        useUpdatedLibPhoneNumber,
      );
      if (opts.length) {
        return opts[0].number;
      }
    }
    return value;
  });
}

type Values<T> = T[keyof T]

export type InferUnion<T extends Record<string | number | symbol, yup.Schema<unknown>>> =
  Values<{ [P in keyof T]: yup.InferType<T[P]> }>

export function lazyUnion<
  T extends Object,
  K extends keyof T
>(
  key: K,
  schemas: T[K] extends string | number | symbol
    ? Record<T[K], yup.Schema<T>>
    : never,
): typeof schemas[T[K]] {
  // @ts-ignore lazyUnion is lying about the return type. At runtime we're using
  // Lazy, but it will resolve to one of the schemas provided. So for the type
  // system I'm just claiming we return one of those schemas.
  return yup.lazy<T>((object: T) => {
    const kind = object[key];
    if (schemas[kind]) {
      return schemas[kind];
    }

    // @ts-ignore
    // Type 'ObjectSchema<{ [x: string]: ...; }>' is not assignable to type 'Schema<T>'
    //
    // I think TS can't verify that type T will have a `[key]`. I think that
    // should be fine, because this is the schema to use if we couldn't find a
    // proper schema.
    return yup.object({
      [key]: yup.string().oneOf(Object.keys(schemas)).required(),
    });
  });

}

/* eslint-disable func-names */

/**
 * Cycles through an array of objects to find duplicate values returned from mapper()
 *
 * paths: List properties to join to make a unique identifier for each item.
 *        Errors will be attached to ALL paths and a BANNER (constant) key for use
 *        with <BannerErrorMessage. Whatever value is found at each path is cast to a
 *        string, trimmed, and lowercased.
 *
 * Must use function() vs () => {} to get proper `this` context
 */
yup.addMethod(yup.array, 'unique', function (
  paths: string[],
) {
  return this.test(
    'unique',
    ({ label }) => intl.formatMessage(messages.errorNotUnique, { label }),
    function (list: string[]) {

      // v is usually a string, but it can be anything.
      const normalize = (v: unknown) => String(v).trim().toLowerCase();
      const mapper = (attachment: unknown) => paths
        .map((path) => normalize(getIn(attachment, path, uuid.v4())))
        .join('|');


      const values: string[] = list.map(mapper);

      const duplicates = values.reduce((acc: string[], v, i, arr): string[] => (
        // If the first instance of this value is not at i, and the item isn't
        // already listed as a duplicate add it to the list
        (arr.indexOf(v) !== i && acc.indexOf(v) === -1)
          ? acc.concat(v)
          : acc
      ), []);

      if (duplicates.length > 0) {
        // The outer error seems to be discarded if you push to `.inner`
        const validationError: yup.ValidationError = this.createError();

        const nestedErrors: yup.ValidationError[] = values.flatMap((value, index) => {
          if (!duplicates.includes(value)) {
            return [];
          }
          return paths.map(
            (path) => this.createError({
              path: `[${this.path}.${index}.${path}]`,
            }),
          );
        });

        validationError.inner.push(this.createError({
          path: `[${this.path}.${BANNER}]`,
        }));
        validationError.inner.push(...nestedErrors);
        return validationError;
      }
      return true;
    },
  );
});

/**
 * Filters a list of items to only include
 * items where at least one of the given fields
 * is not empty. If all the given fields in an item
 * are empty, the item is filtered out.
 * If the post-filter result is empty and less than
 * the length of the original value, the filter is not applied.
 */
yup.addMethod<yup.ArraySchema<object>>(yup.array, 'removeEmpty', function <T extends object> (
  this: yup.ArraySchema<T>,
  fields: Array<keyof T>,
  options: { alwaysFilter?: boolean } = {},
) {
  const filterEmpty = (item: T, fields: Array<keyof T>): boolean => {
    const remove: { [key in keyof T]?: boolean } = {};
    fields.forEach((field) => {
      if (
        field in item && (
          // @ts-ignore TS doesn't believe item[field] has a .length property
          item[field] === null || item[field] === undefined || item[field].length === 0
        )) {
        remove[field] = true;
      }
    });
    if (Object.entries(remove).length === fields.length) {
      return false;
    }
    return true;
  };
  return this.transform((value: Array<any>) => {
    const newValue = value.filter((item) => filterEmpty(item, fields));
    if (options.alwaysFilter) {
      return newValue;
    }

    if (newValue.length === 0 && newValue.length < value.length) {
      return value;
    }
    return newValue;
  });
});

/* eslint-enable func-names, no-template-curly-in-string */


yup.addMethod(yup.string, 'sequence', function makeSequence(functionList) {
  return this.test('sequence', 'Failed Sequence', async function sequence(value: string) {
    try {
      for (const func of functionList) {
        // eslint-disable-next-line no-await-in-loop
        await func(this).validate(value);
      }
    } catch (error: any) {
      return this.createError({
        message: error.message,
      });
    }
    return true;
  });
});
